/**
 * Sending an unauthenticated visitor straight to the identity provider (FR-10).
 *
 * The product has no landing page of its own: the Microsoft and Google buttons,
 * the branding and the password reset all live on the identity provider's own
 * page, so a screen in front of that with one button on it is a click the user
 * has to make before anything can happen. An unauthenticated visit therefore
 * starts the MSAL redirect immediately and `client/src/pages/sign-in.tsx` stays
 * as the FALLBACK — the place a failed, cancelled or deliberately ended session
 * lands, with the reason and something to click.
 *
 * The whole difficulty is not redirecting forever. Three things stop that:
 *
 * - **`/sign-in` never redirects** — with one exception. It is the fallback
 *   route, and it is where MSAL's `postLogoutRedirectUri` puts a user down after
 *   a sign-out, so a person who signs out stays signed out instead of bouncing
 *   back in. An idle expiry (SEC-14) lands there too, with the `expired`
 *   marker's reason and address: one click (owner, round 9 review: one click,
 *   not zero) tries a silent sign-in with that address, then the redirect with
 *   it as the hint (round 10, C3). Only a click calls `ssoSilent`; nothing in
 *   this module or `useAutoSignIn` does. `/sign-in?choose` always shows every
 *   way in and forgets the remembered provider (#1644).
 * - **Only a provider that produced a session is gone back to** (#1644,
 *   `providerChoice.ts`): with several providers, a provider that was chosen
 *   but never let the person in (no account, a cancel, a refusal) is not
 *   remembered, so the next visit shows the choice.
 * - **`/callback` never redirects.** MSAL is still reading the authorization
 *   code out of that URL; starting a second redirect there loses it.
 * - **An attempt leaves a mark.** A return to a signed-out app within
 *   `ATTEMPT_WINDOW_MS` of starting a redirect means the round trip did not
 *   produce a session, and the marker survives the document load that the
 *   redirect caused — which a React ref cannot. The user sees the fallback with
 *   a reason rather than a loop. The mark is cleared only once `/api/v2/me`
 *   answers (B-07): an identity provider that signs a person in whom skyData
 *   then refuses is a round trip that did not produce a session too.
 *
 * The marker lives in `sessionStorage`, so it dies with the tab (the MSAL token
 * cache no longer does since round 9: it is in `localStorage`, and SEC-14 rests on
 * the shared idle clock, `sessionSync.ts`), and every access is wrapped: a browser with
 * site data blocked must still be able to sign in, and the cost of a missing
 * marker there is one extra redirect, not a broken app.
 */

const MARKER_KEY = "skydata-auto-signin";

/** How long a started redirect keeps blocking a second one. */
export const ATTEMPT_WINDOW_MS = 60_000;

export type AutoSignInBlockReason =
  /** A redirect was started and we are back here without a session. */
  | "attempt"
  /** The user ended the session on purpose; nothing may sign them back in. */
  | "signed-out"
  /**
   * The idle timeout ended the session (SEC-14). Blocks a sign-in on a
   * protected route exactly like `signed-out` (the logout navigation is still
   * in flight), and gives `/sign-in` its notice and the address a click signs
   * back in with.
   */
  | "expired";

export interface AutoSignInBlock {
  reason: AutoSignInBlockReason;
  at: number;
  /** `expired` only: the address that was signed in, sent as `login_hint` on the way back. */
  loginHint?: string;
}

function writeMarker(block: AutoSignInBlock): void {
  try {
    sessionStorage.setItem(MARKER_KEY, JSON.stringify(block));
  } catch {
    /* site data blocked; the worst case is one more redirect */
  }
}

/**
 * The marker in force, or null.
 *
 * A sign-out blocks until something clears it, because it is a decision the
 * user made; an attempt blocks only for `ATTEMPT_WINDOW_MS`, because a visit an
 * hour later deserves a fresh try rather than a page telling the user about a
 * redirect they have long forgotten.
 */
export function autoSignInBlock(now: number = Date.now()): AutoSignInBlock | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(MARKER_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const { reason, at, loginHint } = parsed as {
    reason?: unknown;
    at?: unknown;
    loginHint?: unknown;
  };
  if (reason !== "attempt" && reason !== "signed-out" && reason !== "expired") return null;
  if (typeof at !== "number" || !Number.isFinite(at)) return null;
  if (reason === "attempt" && now - at > ATTEMPT_WINDOW_MS) return null;
  return reason === "expired" && typeof loginHint === "string" && loginHint
    ? { reason, at, loginHint }
    : { reason, at };
}

/**
 * Issue #2291: how many returns in a row came back from the identity provider with no session. One
 * return is most often the person pressing Back on the provider's page, which says nothing about
 * the browser. A second one with no session between is the likelier storage problem. A return is
 * counted when the sign-in page actually shows it (`recordSignInReturn`), never at the departure:
 * a redirect that never left, or whose marker expired while the person typed, was not a return.
 * Its own key (not the marker): a click by hand clears the marker, and the count has to survive.
 */
const DEPARTURES_KEY = "skydata-signin-departures";

/** A departure older than this is a visit of its own, not another return in a row. */
export const DEPARTURES_WINDOW_MS = 10 * 60_000;

function readDepartures(now: number): number {
  try {
    const raw = sessionStorage.getItem(DEPARTURES_KEY);
    if (!raw) return 0;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return 0;
    const { count, at } = parsed as { count?: unknown; at?: unknown };
    if (typeof count !== "number" || !Number.isInteger(count) || count < 1) return 0;
    if (typeof at !== "number" || !Number.isFinite(at) || now - at > DEPARTURES_WINDOW_MS) return 0;
    return count;
  } catch {
    return 0;
  }
}

/** Redirects in a row that left and came back with no session (0 when none is on record). */
export function consecutiveSignInReturns(now: number = Date.now()): number {
  return readDepartures(now);
}

/** Called once `/api/v2/me` answers (accepts or refuses) a session: the round trips so far came back. */
export function clearSignInReturns(): void {
  try {
    sessionStorage.removeItem(DEPARTURES_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Called when the sign-in page shows a return with no session; answers how many returns in a row
 * that makes, this one included. Idempotence per return is the caller's (`useAutoSignIn`).
 */
export function recordSignInReturn(now: number = Date.now()): number {
  const count = readDepartures(now) + 1;
  try {
    sessionStorage.setItem(DEPARTURES_KEY, JSON.stringify({ count, at: now }));
  } catch {
    /* site data blocked; the notice then stays the neutral one */
  }
  return count;
}

/** Called just before `loginRedirect` leaves the page. */
export function markAutoSignInAttempt(now: number = Date.now()): void {
  writeMarker({ reason: "attempt", at: now });
}

/**
 * Called when a session ends on purpose (`AuthContext.signOut`).
 *
 * Without this, the moment between "the session is over" and the identity
 * provider's logout navigation is a signed-out app on a protected route, which
 * is precisely the state that starts a sign-in redirect — so a sign-out would
 * race a sign-in and sometimes lose.
 */
export function markSignedOut(now: number = Date.now()): void {
  writeMarker({ reason: "signed-out", at: now });
}

/**
 * Called when the idle timeout ends a session (`AuthContext.signOut("idle")`).
 *
 * It holds off an automatic sign-in for the same reason `markSignedOut` does,
 * but the user did not ask to leave, so `/sign-in` says why and its one click
 * signs back in with the address kept here (silently when the identity
 * provider's session stands).
 */
export function markSessionExpired(loginHint?: string, now: number = Date.now()): void {
  writeMarker(
    loginHint ? { reason: "expired", at: now, loginHint } : { reason: "expired", at: now },
  );
}

/** Called when a session starts, and when the user asks to sign in by hand. */
export function clearAutoSignIn(): void {
  try {
    sessionStorage.removeItem(MARKER_KEY);
  } catch {
    /* nothing to clear */
  }
}

/** What the fallback page says about itself when it was not asked for. */
export interface SignInNotice {
  title: string;
  message: string;
  /** The provider's error code for support (#2006, `signInErrors.ts`), never its text. */
  code?: string;
  /** The id support needs to find this attempt in the logs (API-STANDARD §4). */
  correlationId: string;
}
